// FILE: packages/components/src/admin/ManualGoalLogger.tsx

import React, { useState } from 'react';
import { toast } from '@dopomogai/ui';
import { useLogGoalSnapshot } from '@dopomogai/agent-kit';
import { TextField, Button, Spinner } from '@dopomogai/ui';
import type { GoalWithLatestSnapshot } from '@dopomogai/types';

interface ManualGoalLoggerProps {
  goal: GoalWithLatestSnapshot;
  /**
   * An optional callback function that gets executed after a new
   * value has been successfully logged to the server.
   */
  onLogSuccess?: () => void;
}

export const ManualGoalLogger: React.FC<ManualGoalLoggerProps> = ({ goal, onLogSuccess }) => {
  const [value, setValue] = useState('');
  const { logSnapshot, isLogging, error } = useLogGoalSnapshot();

  const handleLogValue = async (e: React.FormEvent) => {
    e.preventDefault();
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) {
      toast.warn('Please enter a valid number.');
      return;
    }

    const result = await logSnapshot(goal._id, numericValue);

    if (result) {
      toast.success(`New value logged for "${goal.name}".`);
      setValue(''); // Clear input on success

      // --- THE FIX (Part 1): Call the callback if it was provided ---
      if (onLogSuccess) {
        onLogSuccess();
      }
    } else {
      toast.error(error?.message || `Failed to log value for "${goal.name}".`);
    }
  };

  return (
    <form onSubmit={handleLogValue} className="p-4 bg-slate-50 border-t">
      <div className="flex items-end gap-2">
        <TextField
          label="Log New Value"
          type="number"
          name={`log-value-${goal._id}`}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="e.g., 1234.5"
          disabled={isLogging}
          className="flex-grow"
        />
        <Button
          type="submit"
          text="Log"
          disabled={isLogging || !value}
          icon={isLogging ? <Spinner size="sm" /> : 'Check'}
        />
      </div>
    </form>
  );
};